import round from 'lodash/round';

import { Link } from '@mui/material';

import {
  Column,
  LinkBehaviour,
  RatioProgressBar,
  TableHeader,
  Typography,
  Stack,
} from '@cast/design-system';
import { Link as RouterLink } from 'components/router';
import { convertUnit } from '@cast/utils';

import { getRowRankClass } from 'components/table';
import { HourlyConsumedResources } from 'features/cost-report/components/HourlyConsumedResources';
import { NamespacesLinkCell } from 'features/cost-report/components/NamespacesLinkCell';
import {
  NodePricingInfo,
  ValueRenderType,
} from 'features/cost-report/components/NodePricingInfo';
import { WorkloadDetailsLinkCell } from 'features/cost-report/components/WorkloadDetailsLinkCell';
import { formatDailyMetricsCost } from 'features/cost-report/utils/metrics/formatDailyMetricsCost';
import { useGpuMetricsFlag } from 'hooks/useGpuMetricsFlag';

import { WorkloadFilters } from '../../../components/WorkloadFilters';
import { formatMinutesToHours } from '../../_utils';
import { useGpuUtilization } from '../_hooks/useGpuUtilization';
import { NormalizedWorkloadGpuUtilizationSummary } from '../_types';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
  useSearchContext,
} from '@cast/shared-search-ui';

type GpuUtilizationTableProps = {
  cacheKey: string;
  disableWorkloadLink?: boolean;
  getWorkloadLink?: (
    workload: NormalizedWorkloadGpuUtilizationSummary
  ) => string | undefined;
  filtersSize?: 'small';
};

export const GpuUtilizationTable = ({
  cacheKey,
  disableWorkloadLink,
  getWorkloadLink,
  filtersSize,
}: GpuUtilizationTableProps) => {
  const ctx = useSearchContext<NormalizedWorkloadGpuUtilizationSummary>();
  const { topWorkloadsGpuWaste } = useGpuUtilization();
  const showGpuMetrics = useGpuMetricsFlag();

  return (
    <Stack gap={12} data-hc="success">
      <WorkloadFilters size={filtersSize} />

      <SearchableTable<NormalizedWorkloadGpuUtilizationSummary>
        cacheKey={cacheKey}
        testId="workloads-gpu-utilization-summary-table"
        rowKey={(row: NormalizedWorkloadGpuUtilizationSummary) =>
          row.workloadName! + row.namespace! + row.workloadType!
        }
        maxHeight="80vh"
        components={{
          noResults: <NoResults entity="workload" />,
          noData: <NoData entity="workload" />,
          failed: (
            <FailedToLoad title="Failed to load workloads gpu utilization summary" />
          ),
        }}
        getRowClass={({ namespace, workloadName, workloadType }) => {
          const rank = topWorkloadsGpuWaste?.findIndex(
            (item) => item.id === `${workloadName}-${workloadType}-${namespace}`
          );
          return getRowRankClass(rank);
        }}
        outerHeader={
          <TableHeader
            recordsCount={
              <SearchableTableRecordsCount
                title="workload"
                outOf={ctx?.metadata}
              />
            }
          />
        }
      >
        <Column<NormalizedWorkloadGpuUtilizationSummary>
          id="workloadName"
          minWidth={224}
          header="Workload"
          renderCell={(workload) => {
            const { workloadName, namespace, workloadType } = workload;

            if (disableWorkloadLink) {
              return (
                <Typography variant="inherit" fontWeight={500} noWrap>
                  {workloadName}
                </Typography>
              );
            }

            const inventoryLink = getWorkloadLink?.(workload);

            if (inventoryLink) {
              return (
                <Link
                  to={inventoryLink}
                  component={RouterLink}
                  underline="none"
                >
                  <LinkBehaviour variant="inherit" fontWeight={500} noWrap>
                    {workloadName}
                  </LinkBehaviour>
                </Link>
              );
            }

            return (
              <WorkloadDetailsLinkCell
                workload={workloadName}
                namespace={namespace}
                workloadType={workloadType}
              />
            );
          }}
          resizable
          sortable
        />
        <Column id="workloadType" minWidth={96} header="Type" sortable />
        <Column<NormalizedWorkloadGpuUtilizationSummary>
          id="namespace"
          minWidth={122}
          header="Namespace"
          renderCell={({
            namespace,
          }: NormalizedWorkloadGpuUtilizationSummary) => {
            return <NamespacesLinkCell namespace={namespace} />;
          }}
          sortable
        />
        <Column id="podCount" minWidth={88} header="Pods" sortable />
        <Column<NormalizedWorkloadGpuUtilizationSummary>
          id="idleMinutes"
          minWidth={108}
          header="idle time"
          sortable
          renderCell={({ idleMinutes }) =>
            `${formatMinutesToHours(idleMinutes)} h`
          }
        />
        <Column
          id="avgGpuUtil"
          minWidth={171}
          header="AVG. GPU Util."
          headerFullName="Average GPU utilization"
          renderCell={({
            utilization,
          }: NormalizedWorkloadGpuUtilizationSummary) => {
            const usage = round(utilization?.gpuPercent || 0, 2);
            return (
              <Stack gap={1}>
                <Typography variant="P12B">{usage}%</Typography>
                <RatioProgressBar
                  hideBorders
                  size="md"
                  sx={{
                    flexGrow: 1,
                  }}
                  total={100}
                  bars={[
                    {
                      value: usage,
                      color: 'aqua.600',
                    },
                  ]}
                />
              </Stack>
            );
          }}
          sortable
          accessor="utilization.gpuPercent"
          sortingType="number"
        />
        <Column<NormalizedWorkloadGpuUtilizationSummary>
          id="avgWastedMem"
          minWidth={171}
          header="avg wasted mem."
          headerFullName="Average wasted memory"
          renderCell={({ memory }) => {
            return (
              <HourlyConsumedResources
                requests={convertUnit(
                  memory?.availableBytes || 0,
                  'Bytes',
                  'GiB'
                )}
                usage={convertUnit(memory?.usedBytes || 0, 'Bytes', 'GiB')}
                wasted={convertUnit(memory?.wastedBytes || 0, 'Bytes', 'GiB')}
                label="AVERAGE HOURLY WASTED GPU MEMORY"
                unit="GiB"
              />
            );
          }}
          sortable
          accessor="memory.wastedBytes"
          sortingType="number"
        />
        {showGpuMetrics && (
          <Column<NormalizedWorkloadGpuUtilizationSummary>
            id="wasted"
            minWidth={171}
            header="$ wasted"
            infoTooltip="Estimated cost incurred due to underutilized GPU resources. It is calculated by multiplying the duration of GPU idle time by the hourly cost of the GPU instance type."
            renderCell={({
              costImpact,
            }: NormalizedWorkloadGpuUtilizationSummary) => {
              if (!costImpact) {
                return <span data-sortweight="0">-</span>;
              }

              return (
                <Stack width="100%" direction="column" gap={1}>
                  <Typography
                    variant="inherit"
                    fontWeight={600}
                    data-sortweight={costImpact.total}
                  >
                    {formatDailyMetricsCost(costImpact.total)}
                  </Typography>
                  <NodePricingInfo
                    hideTotal
                    fullWidth
                    type={ValueRenderType.Money}
                    onDemand={costImpact?.onDemand}
                    fallback={costImpact?.spotFallback}
                    spot={costImpact?.spot}
                    progressBarSx={{ height: '4px' }}
                    sx={{ lineHeight: '4px' }}
                  />
                </Stack>
              );
            }}
            sortable
            accessor={(row) => row.costImpact?.total ?? 0}
            defaultSort="desc"
          />
        )}
      </SearchableTable>
    </Stack>
  );
};
